import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { LanguageProvider } from "./context/LanguageContext";
import API_URL from "./utils/apiUrl";
import AgeGate from "./components/AgeGate";
import CallCenterApp from "./pages/callcenter/CallCenterApp";

// Dating Site Pages
import DatingLanding from "./pages/dating/Landing";
import DatingAuth from "./pages/dating/Auth";
import ResetPassword from "./pages/dating/ResetPassword";
import ProfileVisitors from "./components/ProfileVisitors";
import DatingDiscover from "./pages/dating/Discover";
import DatingChat from "./pages/dating/Chat";
import DatingProfile from "./pages/dating/Profile";
import DatingCredits from "./pages/dating/Credits";
import DatingSupport from "./pages/dating/Support";

// Admin Pages
import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminSites from "./pages/admin/Sites";
import AdminOperators from "./pages/admin/Operators";
import AdminBulkUpload from "./pages/admin/BulkUpload";
import AdminMassMessage from "./pages/admin/MassMessage";
import AdminCMS from "./pages/admin/CMS";
import AdminAnalytics from "./pages/admin/Analytics";
import AdminOperatorEarnings from "./pages/admin/OperatorEarnings";
import AdminManagement from "./pages/admin/Administrators";
import AdminChat from "./pages/admin/AdminChat";
import AdminSupportChat from "./pages/admin/SupportChat";
import AdminChecker from "./pages/admin/Checker";
import AdminManagedProfiles from "./pages/admin/ManagedProfiles";
import AdminHomepageEditor from "./pages/admin/HomepageEditor";
import AdminAffiliates from "./pages/admin/Affiliates";
import AdminControl from "./pages/admin/AdminControl";
import AbuseReports from "./pages/admin/AbuseReports";
import LanguageSwitch from "./pages/admin/LanguageSwitch";
import OperatorPerformance from "./pages/admin/OperatorPerformance";
import OperatorHours from "./pages/admin/OperatorHours";
import ImageGuardians from "./pages/admin/ImageGuardians";
import FinancialDepartment from "./pages/admin/FinancialDepartment";
import LegalDepartment from "./pages/admin/LegalDepartment";
import HRDepartment from "./pages/admin/HRDepartment";
import DesktopApp from "./pages/admin/DesktopApp";
import AIConfig from "./pages/admin/AIConfig";
import SecurityDashboard from "./pages/admin/SecurityDashboard";
import Downloads from "./pages/admin/Downloads";
import AdminBilling from "./pages/admin/Billing";
import DomainRedirects from "./pages/admin/DomainRedirects";
import UserManagement from "./pages/admin/UserManagement";
import AdminPayslips from "./pages/admin/Payslips";
import AdminSupport from "./pages/admin/Support";

// Operator Pages
import OperatorLogin from "./pages/operator/Login";
import OperatorInbox from "./pages/operator/Inbox";
import OperatorPayslips from "./pages/operator/Payslips";

// Affiliate Pages
import AffiliateLogin from "./pages/affiliate/Login";
import AffiliateDashboard from "./pages/affiliate/Dashboard";
import AffiliatePayslips from "./pages/affiliate/Payslips";

// Lawyer Pages
import LawyerLogin from "./pages/lawyer/Login";
import LawyerDashboard from "./pages/lawyer/Dashboard";

// Standalone Admin Page
import StandaloneAdmin from "./pages/standalone/StandaloneAdmin";

// Master / Platform Owner Pages
import MasterLogin from "./pages/master/MasterLogin";
import MasterDashboard from "./pages/master/MasterDashboard";
import MasterTenants from "./pages/master/MasterTenants";
import MasterAudit from "./pages/master/MasterAudit";
import MasterPlans from "./pages/master/MasterPlans";
import MasterReports from "./pages/master/MasterReports";
import MasterSupport from "./pages/master/MasterSupport";
import MasterCompliance from "./pages/master/MasterCompliance";
import MasterSettings from "./pages/master/MasterSettings";
import MasterImageGuardians from "./pages/master/MasterImageGuardians";
import MasterOperatorHours from "./pages/master/MasterOperatorHours";
import PublicSignup from "./pages/master/PublicSignup";

// Protected Route Component
import ProtectedRoute from "./components/ProtectedRoute";

const APP_MODE = process.env.REACT_APP_MODE || 'full'; // 'full', 'dating', 'command_center', 'standalone', 'hub', 'call_center'

function TitleManager() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/admin') || path.startsWith('/operator') || path.startsWith('/affiliate') || path.startsWith('/lawyer') || path.startsWith('/master')) {
      document.title = APP_MODE === 'hub' ? 'Message Hub' : (APP_MODE === 'call_center' ? 'Priority Talk Call Center' : 'Priority Command');
    } else {
      // Talk + dating sites: always show the site's own domain name in the tab
      const host = window.location.hostname;
      const name = host
        .replace(/^www\./, '')
        .split('.')[0]
        .split('-')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      document.title = name || 'Dating';
    }
  }, [location.pathname]);
  return null;
}

function App() {
  const [ageVerified, setAgeVerified] = useState(() => {
    return sessionStorage.getItem('ageVerified') === 'true';
  });
  const [domainRedirectPending, setDomainRedirectPending] = useState(() => {
    // Check cached redirects synchronously to prevent flash of wrong content
    try {
      const cached = localStorage.getItem('domain_redirects');
      if (cached) {
        const data = JSON.parse(cached);
        const hostname = window.location.hostname;
        const path = window.location.pathname;
        // Never auto-redirect away from operator/admin/master/etc panels
        const onPanel = ['/operator/', '/affiliate/', '/lawyer/', '/admin/', '/master/', '/signup'].some(p => path.startsWith(p));
        if (onPanel) return false;
        const match = (data.redirects || []).find(r =>
          hostname === r.domain || hostname === `www.${r.domain}`
        );
        if (match && !path.startsWith(match.redirect_path.replace('/login', ''))) {
          window.location.replace(match.redirect_path);
          return true; // Block render while redirecting
        }
      }
    } catch {}
    return false;
  });
  const handleAgeVerify = () => {
    sessionStorage.setItem('ageVerified', 'true');
    setAgeVerified(true);
  };
  const showDating = APP_MODE === 'full' || APP_MODE === 'dating' || APP_MODE === 'standalone' || APP_MODE === 'talk';
  const showAdmin = APP_MODE === 'full' || APP_MODE === 'command_center';
  const showStandaloneAdmin = APP_MODE === 'standalone';
  const showHub = APP_MODE === 'hub';
  const showCallCenter = APP_MODE === 'call_center';
  // Skip age gate on every non-end-user route (admin / operator / master / signup etc.).
  // Use a state hook with a popstate listener so SPA navigation updates correctly.
  const isAdminRoute = typeof window !== 'undefined' &&
    ['/admin', '/operator', '/affiliate', '/lawyer', '/master', '/signup', '/login'].some(p => window.location.pathname.startsWith(p));
  // The unified login on "/" is also non-end-user; it dispatches to master/admin/operator.
  // Skip age gate on the bare root path too — otherwise tenant admins / masters can't sign in.
  const isUnifiedLogin = typeof window !== 'undefined' && window.location.pathname === '/';
  const skipAgeGate = APP_MODE === 'command_center' || APP_MODE === 'hub' || APP_MODE === 'call_center' || APP_MODE === 'talk' || isAdminRoute || isUnifiedLogin;

  // Auto-redirect based on domain config from database
  useEffect(() => {
    const hostname = window.location.hostname;
    const path = window.location.pathname;

    // Skip redirect if already on the correct panel path
    const panelPaths = ['/operator/', '/affiliate/', '/lawyer/', '/admin/', '/master/', '/signup'];
    const isOnPanel = panelPaths.some(p => path.startsWith(p));

    const doRedirect = (redirects) => {
      const match = (redirects || []).find(r =>
        hostname === r.domain || hostname === `www.${r.domain}` || hostname.endsWith(`.${r.domain}`)
      );
      if (match) {
        // Only redirect if not already on the correct panel
        if (!path.startsWith(match.redirect_path.replace('/login', ''))) {
          window.location.replace(match.redirect_path);
        }
      }
    };

    // Check cached redirects first (instant redirect)
    try {
      const cached = localStorage.getItem('domain_redirects');
      if (cached) {
        const data = JSON.parse(cached);
        doRedirect(data.redirects);
      }
    } catch {}

    // Fetch fresh redirects from API
    const fetchRedirects = async () => {
      try {
        const res = await fetch(`${API_URL}/api/domain-redirects/config`);
        if (res.ok) {
          const data = await res.json();
          localStorage.setItem('domain_redirects', JSON.stringify(data));
          doRedirect(data.redirects);
        }
      } catch {}
    };

    // Only fetch and potentially redirect if on root or not on any panel
    if (path === '/' || !isOnPanel) {
      fetchRedirects();
    }
  }, []);

  // Live visitor ping — track anonymous front-page traffic (dating/talk/standalone modes).
  useEffect(() => {
    if (APP_MODE === 'command_center' || APP_MODE === 'hub' || APP_MODE === 'call_center') return;
    let sid = localStorage.getItem('visitor_session_id');
    if (!sid) {
      sid = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem('visitor_session_id', sid);
    }
    const ping = () => {
      try {
        fetch(`${API_URL}/api/visitors/ping`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sid, hostname: window.location.host, path: window.location.pathname }),
        }).catch(() => {});
      } catch {}
    };
    ping();
    const id = setInterval(ping, 20000);
    const onVisible = () => { if (!document.hidden) ping(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  // Hub heartbeat — when running as Hub, ping CC every 20s + on visibility change so it stays "online" reliably
  useEffect(() => {
    if (APP_MODE !== 'hub') return;
    const ping = () => {
      try {
        fetch(`${API_URL}/api/hub/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            hostname: window.location.host,
            version: 'ccv13',
            source: 'browser',
          }),
        }).catch(() => {});
      } catch {}
    };
    ping();
    const id = setInterval(ping, 20000);
    const onVisible = () => { if (!document.hidden) ping(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', ping);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', ping);
    };
  }, []);

  // Block render while domain redirect is in progress
  if (domainRedirectPending) {
    return <div style={{background:'#0a0a0a',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'#666',fontFamily:'monospace'}}>Redirecting...</div>
    </div>;
  }

  // Priority Talk Call Center — completely separate standalone app
  if (showCallCenter) {
    return (
      <>
        <Toaster />
        <CallCenterApp />
      </>
    );
  }

  return (
    <AuthProvider>
      <NotificationProvider>
      <LanguageProvider>
      {!skipAgeGate && !ageVerified && <AgeGate onConfirm={handleAgeVerify} />}
      <BrowserRouter>
        <TitleManager />
        {showDating && <ProfileVisitors />}
        <Routes>
          {/* Dating Site Routes */}
          {showDating && <>
          <Route path="/" element={<DatingLanding />} />
          <Route path="/auth" element={<DatingAuth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/discover" element={
            <ProtectedRoute type="user">
              <DatingDiscover />
            </ProtectedRoute>
          } />
          <Route path="/chat" element={
            <ProtectedRoute type="user">
              <DatingChat />
            </ProtectedRoute>
          } />
          <Route path="/chat/:userId" element={
            <ProtectedRoute type="user">
              <DatingChat />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute type="user">
              <DatingProfile />
            </ProtectedRoute>
          } />
          <Route path="/profile/:userId" element={
            <ProtectedRoute type="user">
              <DatingProfile />
            </ProtectedRoute>
          } />
          <Route path="/credits" element={
            <ProtectedRoute type="user">
              <DatingCredits />
            </ProtectedRoute>
          } />
          <Route path="/credits/success" element={
            <ProtectedRoute type="user">
              <DatingCredits />
            </ProtectedRoute>
          } />
          <Route path="/credits/cancel" element={
            <ProtectedRoute type="user">
              <DatingCredits />
            </ProtectedRoute>
          } />
          <Route path="/support" element={
            <ProtectedRoute type="user">
              <DatingSupport />
            </ProtectedRoute>
          } />
          </>}

          {/* Admin Routes */}
          {showAdmin && <>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={
            <ProtectedRoute type="admin">
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/sites" element={
            <ProtectedRoute type="admin">
              <AdminSites />
            </ProtectedRoute>
          } />
          <Route path="/admin/cms/:siteId" element={
            <ProtectedRoute type="admin">
              <AdminCMS />
            </ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute type="admin">
              <AdminAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/admin/operators" element={
            <ProtectedRoute type="admin">
              <AdminOperators />
            </ProtectedRoute>
          } />
          <Route path="/admin/operator-hours" element={
            <ProtectedRoute type="admin">
              <OperatorHours />
            </ProtectedRoute>
          } />
          <Route path="/admin/image-guardians" element={
            <ProtectedRoute type="admin">
              <ImageGuardians />
            </ProtectedRoute>
          } />
          <Route path="/admin/bulk-upload" element={
            <ProtectedRoute type="admin">
              <AdminBulkUpload />
            </ProtectedRoute>
          } />
          <Route path="/admin/mass-message" element={
            <ProtectedRoute type="admin">
              <AdminMassMessage />
            </ProtectedRoute>
          } />
          <Route path="/admin/earnings" element={
            <ProtectedRoute type="admin">
              <AdminOperatorEarnings />
            </ProtectedRoute>
          } />
          <Route path="/admin/administrators" element={
            <ProtectedRoute type="admin">
              <AdminManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/chat" element={
            <ProtectedRoute type="admin">
              <AdminChat />
            </ProtectedRoute>
          } />
          <Route path="/admin/support-chat" element={
            <ProtectedRoute type="admin">
              <AdminSupportChat />
            </ProtectedRoute>
          } />
          <Route path="/admin/support" element={
            <ProtectedRoute type="admin">
              <AdminSupport />
            </ProtectedRoute>
          } />
          <Route path="/admin/checker" element={
            <ProtectedRoute type="admin">
              <AdminChecker />
            </ProtectedRoute>
          } />
          <Route path="/admin/managed-profiles" element={
            <ProtectedRoute type="admin">
              <AdminManagedProfiles />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute type="admin">
              <UserManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/homepage" element={
            <ProtectedRoute type="admin">
              <AdminHomepageEditor />
            </ProtectedRoute>
          } />
          <Route path="/admin/affiliates" element={
            <ProtectedRoute type="admin">
              <AdminAffiliates />
            </ProtectedRoute>
          } />
          <Route path="/admin/admin-control" element={
            <ProtectedRoute type="admin">
              <AdminControl />
            </ProtectedRoute>
          } />
          <Route path="/admin/abuse" element={
            <ProtectedRoute type="admin">
              <AbuseReports />
            </ProtectedRoute>
          } />
          <Route path="/admin/language-switch" element={
            <ProtectedRoute type="admin">
              <LanguageSwitch />
            </ProtectedRoute>
          } />
          <Route path="/admin/operator-performance" element={
            <ProtectedRoute type="admin">
              <OperatorPerformance />
            </ProtectedRoute>
          } />
          <Route path="/admin/financial" element={
            <ProtectedRoute type="admin">
              <FinancialDepartment />
            </ProtectedRoute>
          } />
          <Route path="/admin/legal" element={
            <ProtectedRoute type="admin">
              <LegalDepartment />
            </ProtectedRoute>
          } />
          <Route path="/admin/hr" element={
            <ProtectedRoute type="admin">
              <HRDepartment />
            </ProtectedRoute>
          } />
          <Route path="/admin/desktop-app" element={
            <ProtectedRoute type="admin">
              <DesktopApp />
            </ProtectedRoute>
          } />
          <Route path="/admin/ai-config" element={
            <ProtectedRoute type="admin">
              <AIConfig />
            </ProtectedRoute>
          } />
          <Route path="/admin/security" element={
            <ProtectedRoute type="admin">
              <SecurityDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/downloads" element={
            <ProtectedRoute type="admin">
              <Downloads />
            </ProtectedRoute>
          } />
          <Route path="/admin/billing" element={
            <ProtectedRoute type="admin">
              <AdminBilling />
            </ProtectedRoute>
          } />
          <Route path="/admin/domain-redirects" element={
            <ProtectedRoute type="admin">
              <DomainRedirects />
            </ProtectedRoute>
          } />
          <Route path="/admin/payslips" element={
            <ProtectedRoute type="admin">
              <AdminPayslips />
            </ProtectedRoute>
          } />

          {/* Operator Routes */}
          <Route path="/operator/login" element={<OperatorLogin />} />
          <Route path="/operator" element={
            <ProtectedRoute type="operator">
              <OperatorInbox />
            </ProtectedRoute>
          } />
          <Route path="/operator/inbox" element={
            <ProtectedRoute type="operator">
              <OperatorInbox />
            </ProtectedRoute>
          } />
          <Route path="/operator/payslips" element={
            <ProtectedRoute type="operator">
              <OperatorPayslips />
            </ProtectedRoute>
          } />

          {/* Affiliate Routes */}
          <Route path="/affiliate/login" element={<AffiliateLogin />} />
          <Route path="/affiliate" element={
            <ProtectedRoute type="affiliate">
              <AffiliateDashboard />
            </ProtectedRoute>
          } />
          <Route path="/affiliate/dashboard" element={
            <ProtectedRoute type="affiliate">
              <AffiliateDashboard />
            </ProtectedRoute>
          } />
          <Route path="/affiliate/payslips" element={
            <ProtectedRoute type="affiliate">
              <AffiliatePayslips />
            </ProtectedRoute>
          } />

          {/* Lawyer Routes */}
          <Route path="/lawyer/login" element={<LawyerLogin />} />
          <Route path="/lawyer" element={
            <ProtectedRoute type="lawyer">
              <LawyerDashboard />
            </ProtectedRoute>
          } />
          </>}

          {/* Standalone Admin Routes (simplified admin for single-site) */}
          {showStandaloneAdmin && <>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={
            <ProtectedRoute type="admin">
              <StandaloneAdmin />
            </ProtectedRoute>
          } />
          </>}

          {/* Message Hub Routes (operator inbox on separate domain) */}
          {showHub && <>
          <Route path="/" element={<Navigate to="/operator/login" replace />} />
          <Route path="/operator/login" element={<OperatorLogin />} />
          <Route path="/operator" element={
            <ProtectedRoute type="operator">
              <OperatorInbox />
            </ProtectedRoute>
          } />
          <Route path="/operator/inbox" element={
            <ProtectedRoute type="operator">
              <OperatorInbox />
            </ProtectedRoute>
          } />
          <Route path="/operator/payslips" element={
            <ProtectedRoute type="operator">
              <OperatorPayslips />
            </ProtectedRoute>
          } />
          </>}

          {/* Master / Platform Owner Routes — available regardless of APP_MODE */}
          <Route path="/master/login" element={<MasterLogin />} />
          <Route path="/master" element={<MasterDashboard />} />
          <Route path="/master/tenants" element={<MasterTenants />} />
          <Route path="/master/plans" element={<MasterPlans />} />
          <Route path="/master/compliance" element={<MasterCompliance />} />
          <Route path="/master/support" element={<MasterSupport />} />
          <Route path="/master/reports" element={<MasterReports />} />
          <Route path="/master/audit" element={<MasterAudit />} />
          <Route path="/master/settings" element={<MasterSettings />} />
          <Route path="/master/image-guardians" element={<MasterImageGuardians />} />
          <Route path="/master/operator-hours" element={<MasterOperatorHours />} />

          {/* Public tenant signup */}
          <Route path="/signup" element={<PublicSignup />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to={
            APP_MODE === 'command_center' ? '/admin/login' :
            APP_MODE === 'hub' ? '/operator/login' :
            window.location.pathname.startsWith('/master') ? '/master/login' :
            '/'
          } replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      </LanguageProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
